import { useState } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useAuth } from "@clerk/clerk-react";
import { useLingui } from "@lingui/react";
import { Check, ChevronsUpDown } from "lucide-react";
import { ChildrenService, ApiError } from "@/api";
import { useAuthContext } from "@/contexts/AuthContext";
import { useDebounce } from "@/hooks/useDebounce";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import type { models_Child } from "@/types";

const childSearchKeys = {
  list: (orgId: string | null | undefined, search: string) =>
    ["attendance-child-search", orgId, "ACTIVE", search] as const,
  selected: (orgId: string | null | undefined, id: string) =>
    ["attendance-child-label", orgId, id] as const,
};
const name = (child: models_Child) =>
  `${child.firstname ?? ""} ${child.lastname ?? ""}`.trim();
const retry = (count: number, error: Error) =>
  !(error instanceof ApiError && [401, 403].includes(error.status)) &&
  count < 2;

export function AttendanceChildSelect({
  value,
  onChange,
}: {
  value?: string;
  onChange: (id: string | undefined) => void;
}) {
  const { i18n } = useLingui();
  const { isSignedIn } = useAuth();
  const { isAuthReady, orgId } = useAuthContext();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search.trim());
  const enabled = !!orgId && isSignedIn && isAuthReady;
  const query = useInfiniteQuery({
    queryKey: childSearchKeys.list(orgId, debouncedSearch),
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      const response = await ChildrenService.getApiV1Children(
        50,
        pageParam,
        "ACTIVE",
        debouncedSearch || undefined,
      );
      if (!response.children?.length && (response.total ?? 0) > pageParam) {
        throw new Error("Incomplete active-child search results");
      }
      return response;
    },
    getNextPageParam: (lastPage, _pages, offset) => {
      const next = offset + (lastPage.children?.length ?? 0);
      return next > offset && next < (lastPage.total ?? 0) ? next : undefined;
    },
    enabled: enabled && open,
    retry,
  });
  // Resolve URL selections independently of the search page. A historic link
  // may name an inactive child; keep its label without offering it in results.
  const selected = useQuery({
    queryKey: childSearchKeys.selected(orgId, value ?? ""),
    queryFn: () => ChildrenService.getApiV1Children1(value!),
    enabled: enabled && !!value,
    retry,
  });
  const children = [
    ...new Map(
      (query.data?.pages.flatMap((page) => page.children ?? []) ?? [])
        .filter((child) => child.id)
        .map((child) => [child.id, child]),
    ).values(),
  ];
  const current =
    children.find((child) => child.id === value) ??
    (selected.data?.id === value ? selected.data : undefined);
  const busy = search.trim() !== debouncedSearch || query.isFetching;
  const choose = (id?: string) => {
    onChange(id);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id="filter-child"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between"
        >
          <span className="truncate">
            {current
              ? name(current)
              : value
                ? i18n._({
                    id: "activities.filters.selectedChild",
                    message: "Selected child",
                  })
                : i18n._({
                    id: "activities.filters.allChildren",
                    message: "All children",
                  })}
          </span>
          <ChevronsUpDown
            className="ml-2 h-4 w-4 shrink-0"
            aria-hidden="true"
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 max-w-full p-0">
        <Command shouldFilter={false}>
          <CommandInput
            value={search}
            onValueChange={setSearch}
            placeholder={i18n._({
              id: "activities.filters.searchChildren",
              message: "Search active children...",
            })}
          />
          <CommandList aria-busy={busy}>
            <CommandGroup>
              <CommandItem value="__all__" onSelect={() => choose()}>
                {i18n._({
                  id: "activities.filters.allChildren",
                  message: "All children",
                })}
              </CommandItem>
              {search.trim() === debouncedSearch &&
                children.map((child) => (
                  <CommandItem
                    key={child.id}
                    value={child.id}
                    onSelect={() => choose(child.id)}
                  >
                    <Check
                      aria-hidden="true"
                      className={cn(
                        "mr-2 h-4 w-4",
                        value === child.id ? "opacity-100" : "opacity-0",
                      )}
                    />
                    {name(child)}
                  </CommandItem>
                ))}
            </CommandGroup>
            {busy && (
              <p role="status" className="p-3 text-sm text-muted-foreground">
                {i18n._({
                  id: "common.loading.data",
                  message: "Loading data...",
                })}
              </p>
            )}
            {!busy && !query.isError && children.length === 0 && (
              <p className="p-3 text-sm text-muted-foreground">
                {i18n._({
                  id: "activities.filters.noChildren",
                  message: "No active children found",
                })}
              </p>
            )}
          </CommandList>
        </Command>
        {query.isError && (
          <div role="alert" className="space-y-2 p-3 text-sm">
            <p>
              {i18n._({
                id: "activities.filters.childrenError",
                message: "Children could not be loaded.",
              })}
            </p>
            <Button size="sm" variant="outline" onClick={() => query.refetch()}>
              {i18n._({ id: "common.retry", message: "Try again" })}
            </Button>
          </div>
        )}
        {query.hasNextPage && (
          <div className="border-t p-2">
            <Button
              className="w-full"
              variant="ghost"
              size="sm"
              disabled={busy}
              onClick={() => query.fetchNextPage()}
            >
              {i18n._({
                id: "activities.filters.moreChildren",
                message: "Load more children",
              })}
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
